import { useMemo } from "react";
import Chart, { useChart } from "src/components/chart";
import { alpha, Box, Stack, Typography, Tooltip, TooltipProps, tooltipClasses, styled } from "@mui/material";
import { useClicksAdCostsQuery } from "src/context/api/non-branded/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { fDate } from "src/utils/format-time";
import { fNumber, fPercent, fShortenNumber } from "src/utils/format-number";
import { formatCurrency } from "src/utils/format-currency";
import { useLocales } from "src/locales";
import { Skeleton } from "@mui/material";
import { subWeeks } from "date-fns";
import { useNonBrandedContext } from "./context/use-non-branded";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}));

export default function ClicksAdCostsChart() {
    const { selectedRegion } = useNonBrandedContext();
    const workspace = useCurrentWorkspace();
    const filterStore = useSelector((state: RootState) => state.filters);
    const { t, currentLang: { value: lang } } = useLocales();
    const { data: clicksAdCostsData, isLoading, isFetching, isUninitialized } = useClicksAdCostsQuery({
        workspace_id: workspace?.id as number,
        // last 12 weeks
        start_date: fDate(subWeeks(new Date(), 12), "yyyy-MM-dd"),
        end_date: fDate(new Date(), "yyyy-MM-dd"),
        devices: filterStore.devices,
        countries: selectedRegion ? [selectedRegion] : [],
        platforms: filterStore.platforms,
        interval: "year_week",
        search_terms: filterStore.keywords,
    }, { skip: !workspace?.id || !selectedRegion });

    const { totalClicks, totalAdCost, chartData, categories, dates } = useMemo(() => {
        if (!clicksAdCostsData?.data || clicksAdCostsData?.data?.length === 0) {
            return {
                totalClicks: 0,
                totalAdCost: 0,
                chartData: {
                    organic: [],
                    paidSearch: [],
                    paidShopping: [],
                    adCosts: [],
                    dates: []
                },
                categories: []
            };
        }

        const organic = clicksAdCostsData?.data?.map(item => item.organic_clicks || 0);
        const paidSearch = clicksAdCostsData?.data?.map(item => item.ad_clicks_from_search || 0);
        const paidShopping = clicksAdCostsData?.data?.map(item => item.ad_clicks_from_shopping || 0);
        const adCosts = clicksAdCostsData?.data?.map(item => item.ad_cost || 0);
        const dates = clicksAdCostsData?.data?.map(item => item.date_interval || '') || [];

        if (dates.length < 12) {
            dates.push(...Array((12 - dates.length) > 0 ? 12 - dates.length : 1).fill(null));
        }


        const totalClicks = organic.reduce((sum, val, i) =>
            sum + val + paidSearch[i] + paidShopping[i], 0);
        const totalAdCost = adCosts.reduce((sum, val) => sum + val, 0);

        return {
            totalClicks,
            totalAdCost,
            chartData: { organic: organic || [], paidSearch: paidSearch || [], paidShopping: paidShopping || [], adCosts: adCosts || [] },
            categories: ['Organic', 'Paid Search', 'Paid Shopping'],
            dates: dates?.map((date, i) => date || fDate(subWeeks(new Date(), 12 - i), "yyyy-MM-dd")) || [],
        };
    }, [clicksAdCostsData]);

    const series: ApexCharts.ApexOptions['series'] = [
        {
            name: t("dashboard.nonBrand.columns.adCost"),
            type: 'line',
            data: dates?.map((date, i) => chartData.adCosts[i] || 0) || [],
        },
        {
            name: t("dashboard.nonBrand.columns.organic"),
            type: 'bar',
            data: dates?.map((date, i) => chartData.organic[i] || 0) || [],
        },
        {
            name: t("dashboard.nonBrand.columns.paidSearch"),
            type: 'bar',
            data: dates?.map((date, i) => chartData.paidSearch[i] || 0) || [],
        },
        {
            name: t("dashboard.nonBrand.columns.paidShopping"),
            type: 'bar',
            data: dates?.map((date, i) => chartData.paidShopping[i] || 0) || [],
        },
    ] as any;


    const chartOptions = useChart({
        chart: {
            stacked: true,
            toolbar: { show: false },
        },
        plotOptions: {
            bar: {
                columnWidth: '30px',
                borderRadius: 4,
                hideZeroBarsWhenGrouped: true,
                borderRadiusApplication: 'end',
                borderRadiusWhenStacked: 'last',
            },
        },
        stroke: {
            width: 2,
            curve: 'smooth',
        },
        tooltip: {
            shared: true,
            intersect: false,
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {

                const data = clicksAdCostsData?.data?.[dataPointIndex] || {
                    ad_cost: 0,
                    organic_clicks: 0,
                    ad_clicks_from_search: 0,
                    ad_clicks_from_shopping: 0,
                    date_interval: fDate(subWeeks(new Date(), 12 - dataPointIndex), "yyyy-MM-dd")
                };
                const total = (data.organic_clicks || 0) + (data.ad_clicks_from_search || 0) + (data.ad_clicks_from_shopping || 0);
                const organicPct = total > 0 ? ((data.organic_clicks || 0) / total * 100) : 0;
                const searchPct = total > 0 ? ((data.ad_clicks_from_search || 0) / total * 100) : 0;
                const shoppingPct = total > 0 ? ((data.ad_clicks_from_shopping || 0) / total * 100) : 0;
                const adCostPct = formatCurrency(data.ad_cost || 0, workspace?.currency, lang, true, 1);
                let date = '';
                if (data.date_interval) {
                    try {
                        const dateObj = new Date(data.date_interval);
                        if (!isNaN(dateObj.getTime())) {
                            date = dateObj.toLocaleDateString(lang, {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                            });
                        } else {
                            date = data.date_interval;
                        }
                    } catch {
                        date = data.date_interval;
                    }
                }

                const formatPercent = (val: number) => {
                    const rounded = Math.round(val);
                    return rounded + '%';
                };

                return `
                    <div class="click-split-chart-tooltip">
                        <div class="click-split-chart-tooltip-header">
                            <span class="click-split-chart-tooltip-title">${t("dashboard.nonBrand.columns.clicksSplit")}</span>
                            <span class="click-split-chart-tooltip-date">${date}</span>
                        </div>
                        
                        <div class="click-split-chart-tooltip-row">
                            <div class="click-split-chart-tooltip-indicator ad-cost"></div>
                            <span class="click-split-chart-tooltip-label">${t("dashboard.nonBrand.columns.adCost")}</span>
                            <span class="click-split-chart-tooltip-value">${adCostPct}</span>
                        </div>
                        
                        <div class="click-split-chart-tooltip-row">
                            <div class="click-split-chart-tooltip-indicator organic"></div>
                            <span class="click-split-chart-tooltip-label">${t("dashboard.nonBrand.columns.organic")}</span>
                            <span class="click-split-chart-tooltip-value">${formatPercent(organicPct)}</span>
                        </div>

                        <div class="click-split-chart-tooltip-row">
                            <div class="click-split-chart-tooltip-indicator paid-search"></div>
                            <span class="click-split-chart-tooltip-label">${t("dashboard.nonBrand.columns.paidSearch")}</span>
                            <span class="click-split-chart-tooltip-value">${formatPercent(searchPct)}</span>
                        </div>
                        <div class="click-split-chart-tooltip-row">
                            <div class="click-split-chart-tooltip-indicator paid-shopping"></div>
                            <span class="click-split-chart-tooltip-label">${t("dashboard.nonBrand.columns.paidShopping")}</span>
                            <span class="click-split-chart-tooltip-value">${formatPercent(shoppingPct)}</span>
                        </div>
                    </div>
                `;
            },
        },
        grid: {
            show: false,
        },
        yaxis: [
            {
                show: false,
                min: 0,
                max: Math.max(...chartData.adCosts),
            },
            {
                opposite: true,
                show: false,
                min: 0,
            },
        ],
        legend: {
            show: false,
        },
        colors: [ '#FF9800', '#18A155', '#BC9510', '#F5CE47'],
        fill: {
            opacity: 1,
        },
        xaxis: {
            labels: {
                show: false,
            },
        },
    });
    return (
        <>
            <Stack className="card-top-header" borderBottom={1} borderColor="divider" direction="row" spacing={3} py={1} px={2} justifyContent="space-between">
                <LightTooltip title={t("dashboard.nonBrand.tooltips.clicks")}>
                    <Stack className="card-top-header-item" direction="row" alignItems="center" spacing={1} sx={{ cursor: 'help' }}>
                        <Typography className="card-top-header-item-label" variant="h6" color="text.primary">
                            {t("dashboard.nonBrand.columns.clicks")}
                        </Typography>
                        <Typography className="card-top-header-item-value" variant="h6" color="text.primary">{fShortenNumber(totalClicks)}</Typography>
                    </Stack>
                </LightTooltip>
                <LightTooltip title={t("dashboard.nonBrand.tooltips.adCost")}>
                    <Stack className="card-top-header-item" direction="row" alignItems="center" spacing={1} sx={{ cursor: 'help' }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <svg className="card-top-header-item-indicator" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <line x1="1" y1="7" x2="13" y2="7" stroke="#F46A00" stroke-width="2" stroke-linecap="round" />
                            </svg>
                            <Typography className="card-top-header-item-label" variant="h6" color="text.primary">
                                {t("dashboard.nonBrand.columns.adCost")}
                            </Typography>
                        </Stack>
                        <Typography className="card-top-header-item-value" variant="h6" color="text.primary">
                            {formatCurrency(totalAdCost, workspace?.currency, lang, true, 1)}
                        </Typography>
                    </Stack>
                </LightTooltip>
            </Stack>
            {
                isUninitialized || isLoading || isFetching ? (
                    <Skeleton variant="rectangular" width="100%" height={"100%"} />
                ) : (
                    <Stack width={"inherit"} flex={1} height={149}>
                        {clicksAdCostsData?.data?.length === 0 ? (
                            <Stack height={"100%"} justifyContent="center" alignItems="center">
                                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
                                    {t("dashboard.nonBrand.noClicksAdCostsDataAvailable")}
                                </Typography>
                            </Stack>
                        ) : (
                            <Chart
                                type="bar"
                                series={series}
                                options={chartOptions}
                                height="100%"
                            />
                        )}
                    </Stack>
                )
            }
        </>
    );
}
